import { clearActiveContext } from '@kiao-voice/api-client'

/**
 * Bearer-token storage + change-event primitives for the web app — extracted
 * from `auth-storage` so the shared web API client (`lib/web-api-client`) can
 * depend on JUST these without forming an import cycle (auth-storage's `fetchMe`
 * now goes through that client). `auth-storage` re-exports everything here, so
 * existing `@/features/auth/auth-storage` imports keep working unchanged.
 *
 * Both the internal batch-transcription console and the SaaS dashboard share
 * the same `oat_*` access token, so they must share one storage slot. An earlier
 * iteration used two keys and broke the password-login → /account hand-off;
 * `readStoredToken()` includes a one-time migration so existing logged-in users
 * are not signed out by the rename.
 */
export const TOKEN_STORAGE_KEY = 'kiao-voice:auth-token'

const LEGACY_TOKEN_KEYS = ['kiao-voice:saas-token', 'kiao-voice:batch-transcription-token']

function browserStorage(): Storage | null {
  if (typeof window === 'undefined') return null
  return window.localStorage
}

export function readStoredToken(): string | null {
  const storage = browserStorage()
  if (!storage) return null
  const current = storage.getItem(TOKEN_STORAGE_KEY)
  if (current) return current
  for (const legacy of LEGACY_TOKEN_KEYS) {
    const value = storage.getItem(legacy)
    if (value) {
      storage.setItem(TOKEN_STORAGE_KEY, value)
      storage.removeItem(legacy)
      return value
    }
  }
  return null
}

/**
 * Custom event name fired on the `window` whenever the stored auth token changes
 * (sign-in, sign-out, manual clear). Same-tab listeners subscribe to react
 * in-place; the browser's native `storage` event does NOT fire on the tab that
 * wrote the change, hence this shim.
 */
export const AUTH_TOKEN_CHANGE_EVENT = 'kiao-voice:auth-token-change'

function notifyAuthChange(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(AUTH_TOKEN_CHANGE_EVENT))
}

export function storeToken(token: string): void {
  // Deliberately NO hint-store clearing here: every sign-in mints a fresh
  // opaque token, so "token changed" cannot distinguish an account switch from
  // a same-account re-auth (e.g. the 3DS-return magic-login) — clearing on the
  // byte diff wiped the user's own suggestion store on routine checkouts. The
  // shared-machine leak is closed by `clearStoredToken()` (sign-out and 401
  // eviction both clear); the residual logged-in-A-redeems-B's-magic-link edge
  // needs a user-identity signal to solve and is accepted for now.
  browserStorage()?.setItem(TOKEN_STORAGE_KEY, token)
  notifyAuthChange()
}

export function clearStoredToken(): void {
  const storage = browserStorage()
  if (!storage) return
  // Drop the selected workspace context FIRST, while the token is still in
  // storage — same ordering as before, but it no longer matters for probing:
  // this clear passes `{ notify: false }`, so no subscriber (`useVoiceQuota`,
  // `useOrganizationIdentity`) is told about it and none re-probes. Round-2
  // made `useVoiceQuota` re-probe on EVERY `clear` (including sign-out) to
  // restore server-driven runtime config after a background 403
  // `active_context_forbidden` recovery, but that turned sign-out into a
  // self-inflicted loop: sign-out fires this silent clear → (previously) every
  // subscriber re-probed with no token → 401 → the web API client's 401
  // handler calls `clearStoredToken()` again → clears again → re-probes again.
  // The 403/400 recovery paths still need the notification — the 403
  // `active_context_forbidden` clear inside `packages/api-client`'s
  // `client.ts` and the 400 `invalid_active_context` clear in
  // `web-api-client.ts` both call the bare `clearActiveContext()`, so `notify`
  // stays at its `true` default for them; sign-out never does, since there is
  // no runtime config left to restore for a user who is about to be logged
  // out entirely.
  //
  // Clearing the context also only makes sense paired with a session in the
  // first place — a stale `kiao.activeCtx` outliving its token poisons the
  // next login (the post-login probe re-sends a dead X-Active-Context and
  // 403/400s back to the guest view).
  clearActiveContext({ notify: false })
  // Deliberately NOT clearing the frecency hint store here: this function is
  // also the 401 self-heal eviction path, and tokens expire every 30 days —
  // clearing here would wipe the user's own suggestion data on a routine
  // expiry. The shared-machine leak is closed at the EXPLICIT sign-out
  // (`signOut()` in features/batch-transcription/auth-storage calls
  // `clearHintSuggestionData()`); an expired-then-abandoned session on a
  // shared machine remains an accepted edge that needs a user-identity signal.
  storage.removeItem(TOKEN_STORAGE_KEY)
  // Also clear the legacy keys so a stale session-leftover doesn't resurface on
  // the next page load via the migration path above.
  for (const legacy of LEGACY_TOKEN_KEYS) storage.removeItem(legacy)
  notifyAuthChange()
}

/**
 * Subscribe to auth-token changes. Returns an unsubscribe function. Listens to
 * TWO signals:
 *   1. Our custom AUTH_TOKEN_CHANGE_EVENT — fires same-tab on sign-in/sign-out
 *      (the browser's native `storage` event does NOT fire on the writing tab).
 *   2. The native `storage` event — fires on OTHER tabs when localStorage
 *      changes, so signing in/out on tab A updates tab B's navbar without a
 *      full reload.
 */
export function onAuthTokenChange(handler: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  const onStorage = (event: StorageEvent) => {
    // Ignore unrelated localStorage writes — keep this hot path cheap.
    if (event.key !== null && event.key !== TOKEN_STORAGE_KEY) return
    handler()
  }
  window.addEventListener(AUTH_TOKEN_CHANGE_EVENT, handler)
  window.addEventListener('storage', onStorage)
  return () => {
    window.removeEventListener(AUTH_TOKEN_CHANGE_EVENT, handler)
    window.removeEventListener('storage', onStorage)
  }
}
